import { IBondSecurity } from "@/lib/interfaces and Enums/ENUMBondSecurity";
import { SWAPTION_DIRECTIONS } from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import {
  IBondOptionTicket,
  IBondTicket,
  IFutureTicket,
  IRepoTicket,
  ISwapTicket,
  ISwaptionTicket,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";
import {
  returnBondOptionDateIssue,
  returnBondOptionUnderlyingChanges,
  returnMissingBondOptionTicketEntries,
  returnMissingBondOptionTicketFields,
} from "./bondOptionTicketHelper";
import {
  returnBondSecurityChanges,
  returnMissingBondTicketEntries,
  returnMissingBondTicketFields,
} from "./bondTicketHelper";
import {
  FUTURE_TICKET_DIRECTIONS,
  returnMissingFutureTicketEntries,
  returnMissingFutureTicketFields,
} from "./futureTicketHelper";
import {
  REPO_TICKET_DIRECTIONS,
  returnMissingRepoTicketEntries,
  returnMissingRepoTicketFields,
  returnRepoDateIssue,
} from "./repoTicketHelper";
import {
  SWAP_TICKET_DIRECTIONS,
  isPerLegNominalTicket,
  returnMissingSwapTicketEntries,
  returnMissingSwapTicketFields,
  returnSwapDirectionChanges,
  returnSwapDateIssue,
} from "./swapTicketHelper";
import {
  returnMissingSwaptionTicketEntries,
  returnMissingSwaptionTicketFields,
  returnSwaptionDateIssue,
} from "./swaptionTicketHelper";

// LQC-2941 - one uniform view over each instrument's ticket, so a Trade Creation draft
// leg row can render inline Direction / Size / Security / Settlement cells without a
// component per instrument. The rules themselves stay in each instrument's helper.

/* eslint-disable no-unused-vars */
export type AnyTicket =
  | IBondTicket
  | IFutureTicket
  | IBondOptionTicket
  | ISwapTicket
  | ISwaptionTicket
  | IRepoTicket;

// "bond": picked from GET /Securities/bonds. "future": a contract. "swap": a template swap
// from GET /Securities/swaps, a pre-fill only (LQC-2939).
export type DraftLegSecurityKind = "bond" | "future" | "swap";

export interface IDraftLegAdapter {
  directions: string[];
  securityKind: DraftLegSecurityKind;
  // Futures size in contracts, everything else in face value.
  sizeKey: "nominal" | "contractCount";
  returnDirectionChanges: (ticket: AnyTicket, direction: string) => Partial<AnyTicket>;
  returnBondSelectChanges: (
    ticket: AnyTicket,
    bond: IBondSecurity | undefined,
    isin: string,
  ) => Partial<AnyTicket>;
  // What blocks a save: missing fields, then any date in the wrong order.
  returnBlockingReasons: (ticket: AnyTicket) => string[];
  // Keyed missing fields, so a hovered Save can highlight the rows.
  returnMissingEntries: (ticket: AnyTicket) => { key: string; label: string }[];
  isSizeDisabled: (ticket: AnyTicket) => boolean;
}

const BUY_SELL: string[] = [TradeDirection.Buy, TradeDirection.Sell];
const DATE_REASON = "Check the dates";

const directionOnly = (_ticket: AnyTicket, direction: string) =>
  ({ direction }) as Partial<AnyTicket>;
const bondPick = (_ticket: AnyTicket, bond: IBondSecurity | undefined, isin: string) =>
  ({ isin, securityName: bond?.securityName ?? "" }) as Partial<AnyTicket>;
const neverDisabled = () => false;

function withDateReason(missing: string[], hasDateIssue: boolean): string[] {
  return hasDateIssue ? [...missing, DATE_REASON] : missing;
}

export const DRAFT_LEG_ADAPTERS: Record<TicketInstrument, IDraftLegAdapter> = {
  [TicketInstrument.Bond]: {
    directions: BUY_SELL,
    securityKind: "bond",
    sizeKey: "nominal",
    returnDirectionChanges: directionOnly,
    // Settlement follows the bond's SettlementDays (LQC-2995).
    returnBondSelectChanges: (ticket, bond, isin) =>
      returnBondSecurityChanges(
        (ticket as IBondTicket).tradeDate,
        bond,
        isin,
      ) as Partial<AnyTicket>,
    returnBlockingReasons: (ticket) =>
      returnMissingBondTicketFields(ticket as IBondTicket),
    returnMissingEntries: (ticket) =>
      returnMissingBondTicketEntries(ticket as IBondTicket),
    isSizeDisabled: neverDisabled,
  },
  [TicketInstrument.Future]: {
    directions: FUTURE_TICKET_DIRECTIONS,
    securityKind: "future",
    sizeKey: "contractCount",
    returnDirectionChanges: directionOnly,
    returnBondSelectChanges: () => ({}),
    returnBlockingReasons: (ticket) =>
      returnMissingFutureTicketFields(ticket as IFutureTicket),
    returnMissingEntries: (ticket) =>
      returnMissingFutureTicketEntries(ticket as IFutureTicket),
    isSizeDisabled: neverDisabled,
  },
  [TicketInstrument.BondOption]: {
    directions: BUY_SELL,
    securityKind: "bond",
    sizeKey: "nominal",
    returnDirectionChanges: directionOnly,
    // Same defaults as the Leg Creation ticket: payment currency and Strike.
    returnBondSelectChanges: (_ticket, bond, isin) =>
      returnBondOptionUnderlyingChanges(bond, isin) as Partial<AnyTicket>,
    returnBlockingReasons: (ticket) => {
      const option = ticket as IBondOptionTicket;
      return withDateReason(
        returnMissingBondOptionTicketFields(option),
        !!returnBondOptionDateIssue(option, findBondSecurityByIsin(option.isin)),
      );
    },
    returnMissingEntries: (ticket) =>
      returnMissingBondOptionTicketEntries(ticket as IBondOptionTicket),
    isSizeDisabled: neverDisabled,
  },
  [TicketInstrument.Swap]: {
    directions: SWAP_TICKET_DIRECTIONS,
    securityKind: "swap",
    sizeKey: "nominal",
    // Buy / Sell Inflation reshape the legs (LQC-2991).
    returnDirectionChanges: (ticket, direction) =>
      returnSwapDirectionChanges(
        ticket as ISwapTicket,
        direction as ISwapTicket["direction"],
      ) as Partial<AnyTicket>,
    returnBondSelectChanges: () => ({}),
    returnBlockingReasons: (ticket) =>
      withDateReason(
        returnMissingSwapTicketFields(ticket as ISwapTicket),
        !!returnSwapDateIssue(ticket as ISwapTicket),
      ),
    returnMissingEntries: (ticket) =>
      returnMissingSwapTicketEntries(ticket as ISwapTicket),
    // XCS/XBS capture each leg's nominal in the details (LQC-2971).
    isSizeDisabled: (ticket) => isPerLegNominalTicket(ticket as ISwapTicket),
  },
  [TicketInstrument.Swaption]: {
    directions: SWAPTION_DIRECTIONS,
    securityKind: "swap",
    sizeKey: "nominal",
    returnDirectionChanges: directionOnly,
    returnBondSelectChanges: () => ({}),
    returnBlockingReasons: (ticket) =>
      withDateReason(
        returnMissingSwaptionTicketFields(ticket as ISwaptionTicket),
        !!returnSwaptionDateIssue(ticket as ISwaptionTicket),
      ),
    returnMissingEntries: (ticket) =>
      returnMissingSwaptionTicketEntries(ticket as ISwaptionTicket),
    isSizeDisabled: neverDisabled,
  },
  [TicketInstrument.Repo]: {
    directions: REPO_TICKET_DIRECTIONS,
    securityKind: "bond",
    sizeKey: "nominal",
    returnDirectionChanges: directionOnly,
    returnBondSelectChanges: bondPick,
    returnBlockingReasons: (ticket) =>
      withDateReason(
        returnMissingRepoTicketFields(ticket as IRepoTicket),
        !!returnRepoDateIssue(ticket as IRepoTicket),
      ),
    returnMissingEntries: (ticket) =>
      returnMissingRepoTicketEntries(ticket as IRepoTicket),
    isSizeDisabled: neverDisabled,
  },
};

// The size a row shows, whichever field the instrument keeps it in.
export function returnDraftLegSize(
  ticket: AnyTicket,
  adapter: IDraftLegAdapter,
): number | null {
  return (ticket as unknown as Record<string, number | null>)[adapter.sizeKey] ?? null;
}
